'use client';

import { useInView } from 'framer-motion';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { annotate } from 'rough-notation';
import type { RoughAnnotation } from 'rough-notation/lib/model';
import { cn } from '~/lib/cn';

type AnnotationAction = 'highlight' | 'underline' | 'box' | 'circle' | 'strike-through' | 'crossed-off' | 'bracket';

type BracketSide = 'top' | 'bottom' | 'left' | 'right';

type AnnotationColorVariant = 'success' | 'successLight' | 'warning' | 'error' | 'errorLight' | 'warningLight';

const COLOR_VARIANTS: Record<AnnotationColorVariant, string> = {
    success: '#38c172',
    successLight: '#DAFBE8',
    error: '#e3342f',
    errorLight: '#FBEDED',
    warning: '#f6993f',
    warningLight: '#FDF7E6'
};

export interface HighlighterProps extends React.HTMLAttributes<HTMLSpanElement> {
    action?: AnnotationAction;
    variant?: AnnotationColorVariant;
    strokeWidth?: number;
    animationDuration?: number;
    iterations?: number;
    padding?: number;
    multiline?: boolean;
    isView?: boolean;
    brackets?: BracketSide[];
}

export default function Highlighter({
    children,
    action = 'highlight',
    variant = 'success',
    strokeWidth = 1.5,
    animationDuration = 600,
    iterations = 1,
    padding = 2,
    multiline = true,
    isView = true,
    brackets,

    className,
    ...rest
}: HighlighterProps) {
    const elementRef = useRef<HTMLSpanElement>(null);
    const annotationRef = useRef<RoughAnnotation | null>(null);
    const color = COLOR_VARIANTS[variant];

    const isInView = useInView(elementRef, {
        once: true,
        amount: 0.6
    });

    useEffect(() => {
        if (isView && !isInView) return;

        const element = elementRef.current;
        if (!element) return;

        annotationRef.current?.remove();

        const annotation = annotate(element, {
            type: action,
            color: color,
            strokeWidth,
            animationDuration,
            iterations,
            padding,
            multiline,
            ...(action === 'bracket' && brackets ? { brackets } : {})
        });

        annotationRef.current = annotation;
        annotation.show();

        const resizeObserver = new ResizeObserver(() => {
            if (!annotationRef.current) return;
            requestAnimationFrame(() => {
                annotationRef.current?.hide();
                annotationRef.current?.show();
            });
        });

        resizeObserver.observe(element);

        return () => {
            annotationRef.current?.remove();
            resizeObserver.disconnect();
        };
    }, [isView, isInView, action, color, strokeWidth, animationDuration, iterations, padding, multiline, brackets]);

    return (
        <span ref={elementRef} className={cn('relative inline-block bg-transparent', className)} {...rest}>
            {children}
        </span>
    );
}
